import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import './App.css';
import { AddContact, ContactList, ContactDetails } from './features/contacts';
import { AddAsset, Assets, AssetDetails, AssetSettingsPage, LegacyAssetAddRedirect, AssetTypeRoute, AssetTypeListRedirect } from './features/assets';
import { AssetSettingsProvider, useAssetsModuleVisible } from './features/assets/assetsModule';
import { AddProduct, ProductCategories, ProductDetails, Products } from './features/products';
import { AddService, ServiceCategories, ServiceDetails, Services } from './features/services';
import { AddInvoice, InvoiceDetails, InvoiceSettingsPage, Invoices } from './features/invoices';
import { PaymentAutomationsPage, PaymentsLayout, PaymentSettingsPage, PaymentsPage } from './features/payments';
import { ConversationsPage, ChatProvider } from './features/conversations';
import { CalendarPage } from './features/calendar';
import { CalendarEventStatusesPage } from './features/calendar/components/CalendarEventStatusesPage';
import { EmployeeWeekSchedulePage, RosterPage } from './features/rostering';
import { FieldOperationsPage, FieldOperationsSettingsPage } from './features/field-operations';
import { FieldOperationsSettingsProvider, useFieldOperationsSettings } from './features/field-operations/fieldOperationsModule';
import { ConsultationDetailsPage, ConsultationFieldDefinitionsPage, ConsultationTemplatesPage, ConsultationTypesPage, ConsultationsPage, AppointmentConsultationRedirect, CreateConsultationPage } from './features/consultations';
import { DashboardPage } from './features/dashboard';
import {
  ReminderDefinitionEditPage,
  ReminderDefinitionsPage,
  ReminderEngineLayout,
  ReminderScheduledPage,
  ReminderTemplatesPage,
} from './features/reminder-engine';
import { TaskList, TaskEditor } from './features/tasks';
import { KnowledgeBasePage } from './features/knowledge';
import { OrchestratorsPage } from './features/ai-orchestrators';
import { ApiToolsPage } from './features/api-tools';
import { ToolsPage } from './features/tools';
import { AiTransferPanel } from './features/tools/components/AiTransferPanel';
import { McpToolsPage } from './features/mcp/components/McpToolsPage';
import { ToolPluginsPage } from './features/ai-tool-plugins';
import { SkillsPage } from './features/ai-skills';
import { CommandsPage } from './features/ai-commands';
import { HooksPage } from './features/ai-hooks';
import { RulesHubPage } from './features/ai-rules-hub';
import { AiActivityPage } from './features/ai-activity';
import { AiUsagePage } from './features/ai-usage';
import { PlansPage } from './features/billing';
import { KnowledgeDynamicToolsPanel } from './features/knowledge/components/KnowledgeDynamicToolsPanel';
import { AddEmployee, EmployeeDetails, MyProfilePage, TeamMembers, RolesPage, SecurityPage } from './features/admin';
import { AiSettingsPage } from './features/admin/components/AiPromptConfigPage';
import { CompaniesPage } from './features/companies';
import { ConnectorsPage } from './features/connectors';
import { CallCenter, CallsPage, InboundCallPopPage } from './features/calling';
import { AutomationsPage } from './features/automations';
import { WebWidgetDemoPage } from './features/webWidget';
import RefreshTokenOperationsPage from './features/admin/components/RefreshTokenOperationsPage';
import { Sidebar, Header, FloatingChat, Spinner } from './shared/components';
import { LoginPage } from './features/auth/components/LoginPage';
import { SsoCompletePage } from './features/auth/components/SsoCompletePage';
import { ForgotPasswordPage } from './features/auth/components/ForgotPasswordPage';
import { ResetPasswordPage } from './features/auth/components/ResetPasswordPage';
import { useAuth } from './features/auth/context/AuthContext';
import {
  CreateWorkspacePage,
  GlobalLoginPage,
  SignUpPage,
  WorkspaceHandoffPage,
  WorkspacePickerPage,
  WorkspaceSlugRedirect,
} from './features/workspaces';
import { isGlobalAuthHost } from './shared/config/workspaceHost';
import { LegalDocumentPage } from './legal/LegalDocumentPage';
import { buildLoginPath, resolveReturnPath } from './features/auth/utils/loginReturnUrl';
import { PermissionCodes, PermissionScopes } from './features/auth/permissions';
import { useTranslation } from 'react-i18next';

type PermissionGuardProps = {
  anyOf?: readonly string[];
  allOf?: readonly string[];
  children: React.ReactElement;
  redirectTo?: string;
};

const PermissionGuard: React.FC<PermissionGuardProps> = ({ anyOf, allOf, children, redirectTo = '/unauthorized' }) => {
  const { hasPermission } = useAuth();
  const passesAny = !anyOf || anyOf.length === 0 || hasPermission(anyOf);
  const passesAll = !allOf || allOf.every((permission) => hasPermission(permission));
  return passesAny && passesAll ? children : <Navigate to={redirectTo} replace />;
};

const UnauthorizedPage: React.FC = () => {
  const { t } = useTranslation('app');

  return (
    <div className="company-section" style={{ marginTop: 24 }}>
      <h3>{t('accessDenied')}</h3>
      <p>{t('noPermission')}</p>
    </div>
  );
};

const LegacyContactDetailsRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={id ? `/contacts/${id}${location.search}` : '/contacts'} replace />;
};

const LegacyAssetDetailsRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isAssetsModuleVisible = useAssetsModuleVisible();
  if (!isAssetsModuleVisible) {
    return <Navigate to="/" replace />;
  }

  const suffix = location.pathname.endsWith('/details') ? '/details' : '';
  return <Navigate to={id ? `/assets/${id}${suffix}${location.search}` : '/assets'} replace />;
};

const LegacyAssetConsultationRedirect: React.FC = () => {
  const { petId: legacyAssetId, appointmentId } = useParams<{ petId: string; appointmentId: string }>();
  const location = useLocation();
  const isAssetsModuleVisible = useAssetsModuleVisible();
  if (!legacyAssetId || !appointmentId) {
    return <Navigate to={isAssetsModuleVisible ? '/assets' : '/calendar'} replace />;
  }

  return (
    <Navigate
      to={`/appointments/${appointmentId}/consultation${location.search}`}
      state={{ returnTo: isAssetsModuleVisible ? `/assets/${legacyAssetId}?tab=visits` : '/calendar' }}
      replace
    />
  );
};

const LegacyConsultationWorkspaceRedirect: React.FC = () => {
  const { assetId, appointmentId } = useParams<{ assetId: string; appointmentId: string }>();
  const location = useLocation();
  const state = location.state as { returnTo?: string } | null;
  const isAssetsModuleVisible = useAssetsModuleVisible();

  if (!appointmentId) {
    return <Navigate to={isAssetsModuleVisible ? '/assets' : '/calendar'} replace />;
  }

  return (
    <Navigate
      to={`/appointments/${appointmentId}/consultation${location.search}`}
      state={{ returnTo: state?.returnTo || (assetId ? `/assets/${assetId}?tab=visits` : '/calendar') }}
      replace
    />
  );
};

/** Automations left the admin area; keep the tab selection when following an old link. */
const RedirectToAutomations: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/automations${location.search}`} replace />;
};

const TaskCreateRedirect: React.FC = () => {
  const location = useLocation();
  const hasQuery = Boolean(location.search && location.search.length > 1);
  return <Navigate to={`/tasks${hasQuery ? `${location.search}&create=1` : '?create=1'}`} replace />;
};

const LoginRedirect: React.FC = () => {
  const location = useLocation();
  const returnUrl = resolveReturnPath(location.search);
  return <Navigate to={returnUrl} replace />;
};

const FieldOperationsRoute: React.FC = () => {
  const { hasPermission } = useAuth();
  const { isModuleEnabled, loading } = useFieldOperationsSettings();

  if (!loading && !isModuleEnabled && !hasPermission(PermissionScopes.fieldOperationsSettings)) {
    return <Navigate to="/" replace />;
  }

  return <FieldOperationsPage />;
};

function App() {
  return (
    <AssetSettingsProvider>
      <FieldOperationsSettingsProvider>
        <AppShell />
      </FieldOperationsSettingsProvider>
    </AssetSettingsProvider>
  );
}

/**
 * Global auth host: sign in once, pick a workspace, then hand off to that workspace's own hostname.
 * No product route runs here because the host resolves no tenant.
 */
const GlobalAuthRoutes: React.FC = () => (
  <Routes>
    <Route path="/legal/:documentId" element={<LegalDocumentPage />} />
    <Route path="/login" element={<GlobalLoginPage />} />
    <Route path="/signup" element={<SignUpPage />} />
    <Route path="/workspaces" element={<WorkspacePickerPage />} />
    <Route path="/workspaces/new" element={<CreateWorkspacePage />} />
    <Route path="/login/handoff" element={<WorkspaceHandoffPage />} />
    <Route path="/w/:slug" element={<WorkspaceSlugRedirect />} />
    <Route path="*" element={<Navigate to="/login" replace />} />
  </Routes>
);

function AppShell() {
  const { hasPermission, isAuthenticated, isLoading } = useAuth();
  const onGlobalAuthHost = isGlobalAuthHost();
  const isAssetsModuleVisible = useAssetsModuleVisible();
  const canViewDashboard = hasPermission(PermissionScopes.conversationsRead)
    || hasPermission(PermissionScopes.consultationsRead)
    || hasPermission(PermissionScopes.tasksRead);
  const canViewKnowledge = hasPermission(PermissionScopes.knowledgeRead);
  const canViewAiOrchestrators = hasPermission(PermissionScopes.aiOrchestratorsRead);
  const canViewApiTools = hasPermission(PermissionScopes.apiToolsRead);
  const canViewDataTools = hasPermission(PermissionCodes.knowledgeManage);
  const canViewMcpTools = hasPermission(PermissionScopes.mcpConnectorsRead);
  const canViewToolPlugins = hasPermission(PermissionScopes.aiToolPluginsRead);
  const canViewAiConfig = hasPermission(PermissionScopes.aiConfigRead);
  const canViewTools = canViewApiTools
    || canViewDataTools
    || canViewMcpTools
    || canViewToolPlugins
    || canViewAiConfig;
  const canViewAiActivity = hasPermission([PermissionCodes.adminAccess]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
    } catch {}
  }, [sidebarCollapsed]);

  const aiHomePath = (() => {
    if (canViewKnowledge) return '/ai/knowledge';
    if (canViewAiOrchestrators) return '/ai/subagents';
    if (canViewTools) return '/ai/tools';
    if (canViewAiConfig) return '/ai/config';
    if (canViewAiActivity) return '/ai/activity';
    return '/unauthorized';
  })();

  const toolsDefaultPath = (() => {
    if (canViewApiTools) return '/ai/tools/api';
    if (canViewDataTools) return '/ai/tools/data';
    if (canViewMcpTools) return '/ai/tools/mcp';
    if (canViewToolPlugins) return '/ai/tools/plugins';
    if (canViewAiConfig) return '/ai/tools/skills';
    return '/unauthorized';
  })();

  const defaultHomePath = (() => {
    if (canViewDashboard) return '/dashboard';
    if (hasPermission(PermissionScopes.conversationsRead)) return '/conversations';
    if (isAssetsModuleVisible && hasPermission(PermissionScopes.assetsRead)) return '/assets';
    if (hasPermission(PermissionScopes.productsRead)) return '/products';
    if (hasPermission(PermissionScopes.servicesRead)) return '/services';
    if (hasPermission(PermissionScopes.consultationsRead)) return '/consultations';
    if (hasPermission(PermissionScopes.calendarRead)) return '/calendar';
    if (hasPermission(PermissionScopes.tasksRead)) return '/tasks';
    if (canViewKnowledge || canViewAiOrchestrators || canViewTools || canViewAiConfig || canViewAiActivity) return aiHomePath;
    if (hasPermission(PermissionScopes.companiesRead)) return '/admin/companies';
    if (hasPermission(PermissionScopes.employeesRead)) return '/members';
    if (hasPermission(PermissionScopes.contactsRead)) return '/contacts';
    if (hasPermission(PermissionScopes.rolesRead)) return '/admin/roles';
    if (hasPermission(PermissionCodes.adminAccess)) return '/admin/security';
    return '/unauthorized';
  })();

  const adminHomePath = (() => {
    if (hasPermission(PermissionScopes.companiesRead)) return '/admin/companies';
    if (hasPermission(PermissionScopes.rolesRead)) return '/admin/roles';
    if (hasPermission(PermissionCodes.adminAccess)) return '/admin/security';
    if (hasPermission(PermissionScopes.employeesRead)) return '/members';
    return '/unauthorized';
  })();

  return (
    <Router>
      {isLoading ? (
        <div className="app-boot-loading">
          <Spinner size={44} text="Loading" />
        </div>
      ) : onGlobalAuthHost && !isAuthenticated ? (
        <GlobalAuthRoutes />
      ) : (
      <Routes>
        <Route path="/legal/:documentId" element={<LegalDocumentPage />} />
        <Route
          path="/login"
          element={isAuthenticated ? <LoginRedirect /> : <LoginPage />}
        />

        <Route path="/login/handoff" element={<WorkspaceHandoffPage />} />

        <Route path="/w/:slug" element={<WorkspaceSlugRedirect />} />

        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
        />

        <Route
          path="/reset-password"
          element={isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordPage />}
        />

        <Route
          path="/login/sso-complete"
          element={<SsoCompletePage />}
        />

        <Route
          path="*"
          element={
            !isAuthenticated ? (
              <Navigate
                to={buildLoginPath(
                  window.location.pathname,
                  window.location.search,
                  window.location.hash,
                )}
                replace
              />
            ) : (
              <ChatProvider>
                <div className={`App${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
                  <Header />
                  <div className={`app-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                    <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
                    <main className="main-content">
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <Navigate to={defaultHomePath} replace />
                          }
                        />
                        <Route path="/owners" element={
                          <PermissionGuard anyOf={PermissionScopes.contactsRead}>
                            <Navigate to="/contacts" replace />
                          </PermissionGuard>
                        } />
                        <Route path="/owners/add" element={
                          <PermissionGuard anyOf={PermissionScopes.contactsCreate}>
                            <Navigate to="/contacts/add" replace />
                          </PermissionGuard>
                        } />
                        <Route path="/owners/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.contactsRead}>
                            <LegacyContactDetailsRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/contacts" element={
                          <PermissionGuard anyOf={PermissionScopes.contactsRead}>
                            <ContactList />
                          </PermissionGuard>
                        } />
                        <Route path="/contacts/add" element={
                          <PermissionGuard anyOf={PermissionScopes.contactsCreate}>
                            <AddContact />
                          </PermissionGuard>
                        } />
                        <Route path="/contacts/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.contactsRead}>
                            <ContactDetails />
                          </PermissionGuard>
                        } />
                        <Route path="/contact/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.contactsRead}>
                            <LegacyContactDetailsRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/pets" element={<Navigate to="/assets?type=pets" replace />} />
                        <Route path="/pets/add" element={<Navigate to="/assets/pets/add" replace />} />
                        <Route path="/pets/:id" element={<LegacyAssetDetailsRedirect />} />
                        <Route path="/pets/:id/details" element={<LegacyAssetDetailsRedirect />} />
                        <Route path="/assets" element={
                          <PermissionGuard anyOf={PermissionScopes.assetsRead}>
                            <Assets />
                          </PermissionGuard>
                        } />
                        <Route path="/assets/add" element={
                          <PermissionGuard anyOf={PermissionScopes.assetsCreate}>
                            <LegacyAssetAddRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/assets/settings" element={
                          <PermissionGuard anyOf={PermissionScopes.assetsRead}>
                            <AssetSettingsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/assets/:typeSlug/add" element={
                          <PermissionGuard anyOf={PermissionScopes.assetsCreate}>
                            <AssetTypeRoute>
                              <AddAsset />
                            </AssetTypeRoute>
                          </PermissionGuard>
                        } />
                        <Route path="/assets/:typeSlug/:id/details" element={
                          <PermissionGuard anyOf={PermissionScopes.assetsRead}>
                            <AssetTypeRoute>
                              <AssetDetails />
                            </AssetTypeRoute>
                          </PermissionGuard>
                        } />
                        <Route path="/assets/:typeSlug/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.assetsRead}>
                            <AssetTypeRoute>
                              <AssetDetails />
                            </AssetTypeRoute>
                          </PermissionGuard>
                        } />
                        <Route path="/assets/:typeSlug" element={
                          <PermissionGuard anyOf={PermissionScopes.assetsRead}>
                            <AssetTypeListRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/products" element={
                          <PermissionGuard anyOf={PermissionScopes.productsRead}>
                            <Products />
                          </PermissionGuard>
                        } />
                        <Route path="/products/add" element={
                          <PermissionGuard anyOf={PermissionScopes.productsCreate}>
                            <AddProduct />
                          </PermissionGuard>
                        } />
                        <Route path="/products/categories" element={
                          <PermissionGuard anyOf={PermissionScopes.productsRead}>
                            <ProductCategories />
                          </PermissionGuard>
                        } />
                        <Route path="/products/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.productsRead}>
                            <ProductDetails />
                          </PermissionGuard>
                        } />
                        <Route path="/services" element={
                          <PermissionGuard anyOf={PermissionScopes.servicesRead}>
                            <Services />
                          </PermissionGuard>
                        } />
                        <Route path="/services/add" element={
                          <PermissionGuard anyOf={PermissionScopes.servicesCreate}>
                            <AddService />
                          </PermissionGuard>
                        } />
                        <Route path="/services/categories" element={
                          <PermissionGuard anyOf={PermissionScopes.servicesRead}>
                            <ServiceCategories />
                          </PermissionGuard>
                        } />
                        <Route path="/services/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.servicesRead}>
                            <ServiceDetails />
                          </PermissionGuard>
                        } />
                        <Route path="/invoices" element={
                          <PermissionGuard anyOf={PermissionScopes.invoicesRead}>
                            <Invoices />
                          </PermissionGuard>
                        } />
                        <Route path="/invoices/add" element={
                          <PermissionGuard anyOf={PermissionScopes.invoicesCreate}>
                            <AddInvoice />
                          </PermissionGuard>
                        } />
                        <Route path="/invoices/settings" element={
                          <PermissionGuard anyOf={PermissionScopes.invoicesRead}>
                            <InvoiceSettingsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/invoices/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.invoicesRead}>
                            <InvoiceDetails />
                          </PermissionGuard>
                        } />
                        <Route path="/payments" element={
                          <PermissionGuard anyOf={PermissionScopes.paymentsRead}>
                            <PaymentsLayout />
                          </PermissionGuard>
                        }>
                          <Route index element={<PaymentsPage />} />
                          <Route path="settings" element={
                            <PermissionGuard anyOf={PermissionScopes.paymentsSettings}>
                              <PaymentSettingsPage />
                            </PermissionGuard>
                          } />
                          <Route path="automations" element={
                            <PermissionGuard anyOf={PermissionScopes.paymentsSettings}>
                              <PaymentAutomationsPage />
                            </PermissionGuard>
                          } />
                        </Route>
                        <Route path="/pets/:petId/appointment/:appointmentId/consultation" element={
                          <PermissionGuard allOf={[PermissionCodes.calendarRead, PermissionCodes.consultationsRead]}>
                            <LegacyAssetConsultationRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/assets/:assetId/appointment/:appointmentId/consultation" element={
                          <PermissionGuard allOf={[PermissionCodes.calendarRead, PermissionCodes.consultationsRead]}>
                            <LegacyConsultationWorkspaceRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/appointments/:appointmentId/consultation" element={
                          <PermissionGuard allOf={[PermissionCodes.calendarRead, PermissionCodes.consultationsRead]}>
                            <AppointmentConsultationRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/calendar" element={
                          <PermissionGuard anyOf={PermissionScopes.calendarRead}>
                            <CalendarPage />
                          </PermissionGuard>
                        } />
                        <Route path="/calendar/event-statuses" element={
                          <PermissionGuard anyOf={PermissionScopes.calendarStatusesManage}>
                            <CalendarEventStatusesPage />
                          </PermissionGuard>
                        } />
                        <Route path="/rostering" element={
                          <PermissionGuard anyOf={PermissionScopes.rosteringRead}>
                            <RosterPage />
                          </PermissionGuard>
                        } />
                        <Route path="/rostering/employees/:employeeId" element={
                          <PermissionGuard anyOf={PermissionScopes.rosteringRead}>
                            <EmployeeWeekSchedulePage />
                          </PermissionGuard>
                        } />
                        <Route path="/field-operations" element={
                          <PermissionGuard anyOf={[...PermissionScopes.fieldOperationsCheckIn, ...PermissionScopes.fieldOperationsSettings]}>
                            <FieldOperationsRoute />
                          </PermissionGuard>
                        } />
                        <Route path="/field-operations/settings" element={
                          <PermissionGuard anyOf={PermissionScopes.fieldOperationsSettings}>
                            <FieldOperationsSettingsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/consultations" element={
                          <PermissionGuard anyOf={PermissionScopes.consultationsRead}>
                            <ConsultationsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/consultations/new" element={
                          <PermissionGuard anyOf={PermissionScopes.consultationsManage}>
                            <CreateConsultationPage />
                          </PermissionGuard>
                        } />
                        <Route path="/consultations/field-definitions" element={
                          <PermissionGuard anyOf={PermissionScopes.consultationsManage}>
                            <ConsultationFieldDefinitionsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/consultations/templates" element={
                          <PermissionGuard anyOf={PermissionScopes.consultationsManage}>
                            <ConsultationTemplatesPage />
                          </PermissionGuard>
                        } />
                        <Route path="/consultations/types" element={
                          <PermissionGuard anyOf={PermissionScopes.consultationsManage}>
                            <ConsultationTypesPage />
                          </PermissionGuard>
                        } />
                        <Route path="/consultations/:consultationId" element={
                          <PermissionGuard anyOf={PermissionScopes.consultationsRead}>
                            <ConsultationDetailsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/tasks" element={
                          <PermissionGuard anyOf={PermissionScopes.tasksRead}>
                            <TaskList />
                          </PermissionGuard>
                        } />
                        <Route path="/reminder-engine" element={
                          <PermissionGuard anyOf={PermissionScopes.remindersRead}>
                            <ReminderEngineLayout />
                          </PermissionGuard>
                        }>
                          <Route index element={<Navigate to="definitions" replace />} />
                          <Route path="definitions" element={<ReminderDefinitionsPage />} />
                          <Route path="templates" element={<ReminderTemplatesPage />} />
                          <Route path="scheduled" element={<ReminderScheduledPage />} />
                        </Route>
                        <Route path="/reminder-engine/definitions/:definitionKey/edit" element={
                          <PermissionGuard anyOf={PermissionScopes.remindersManage}>
                            <ReminderDefinitionEditPage />
                          </PermissionGuard>
                        } />
                        <Route path="/dashboard" element={
                          <PermissionGuard anyOf={[
                            ...PermissionScopes.tasksRead,
                            ...PermissionScopes.conversationsRead,
                            ...PermissionScopes.consultationsRead,
                          ]}>
                            <DashboardPage />
                          </PermissionGuard>
                        } />
                        <Route path="/ai" element={<Navigate to={aiHomePath} replace />} />
                        <Route path="/ai/knowledge" element={
                          <PermissionGuard anyOf={PermissionScopes.knowledgeRead}>
                            <KnowledgeBasePage />
                          </PermissionGuard>
                        } />
                        <Route path="/ai/subagents" element={
                          <PermissionGuard anyOf={PermissionScopes.aiOrchestratorsRead}>
                            <OrchestratorsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/ai/orchestrators" element={<Navigate to="/ai/subagents" replace />} />
                        <Route path="/ai/tools" element={
                          <PermissionGuard anyOf={[
                            ...PermissionScopes.apiToolsRead,
                            PermissionCodes.knowledgeManage,
                            ...PermissionScopes.mcpConnectorsRead,
                            ...PermissionScopes.aiToolPluginsRead,
                            ...PermissionScopes.aiConfigRead,
                          ]}>
                            <ToolsPage />
                          </PermissionGuard>
                        }>
                          <Route index element={<Navigate to={toolsDefaultPath} replace />} />
                          <Route path="api" element={
                            <PermissionGuard anyOf={PermissionScopes.apiToolsRead}>
                              <ApiToolsPage embedded />
                            </PermissionGuard>
                          } />
                          <Route path="data" element={
                            <PermissionGuard allOf={[PermissionCodes.knowledgeManage]}>
                              <KnowledgeDynamicToolsPanel showPageHeader={false} />
                            </PermissionGuard>
                          } />
                          <Route path="mcp" element={
                            <PermissionGuard anyOf={PermissionScopes.mcpConnectorsRead}>
                              <McpToolsPage />
                            </PermissionGuard>
                          } />
                          <Route path="plugins" element={
                            <PermissionGuard anyOf={PermissionScopes.aiToolPluginsRead}>
                              <ToolPluginsPage />
                            </PermissionGuard>
                          } />
                          <Route path="skills" element={
                            <PermissionGuard anyOf={PermissionScopes.aiConfigRead}>
                              <SkillsPage />
                            </PermissionGuard>
                          } />
                          <Route path="commands" element={
                            <PermissionGuard anyOf={PermissionScopes.aiConfigRead}>
                              <CommandsPage />
                            </PermissionGuard>
                          } />
                          <Route path="hooks" element={
                            <PermissionGuard anyOf={PermissionScopes.aiConfigRead}>
                              <HooksPage />
                            </PermissionGuard>
                          } />
                          <Route path="rules" element={
                            <PermissionGuard anyOf={PermissionScopes.aiConfigRead}>
                              <RulesHubPage />
                            </PermissionGuard>
                          } />
                          <Route path="transfer" element={
                            <PermissionGuard anyOf={PermissionScopes.aiConfigRead}>
                              <AiTransferPanel />
                            </PermissionGuard>
                          } />
                        </Route>
                        <Route path="/ai/api-tools" element={<Navigate to="/ai/tools/api" replace />} />
                        <Route path="/ai/config" element={
                          <PermissionGuard anyOf={PermissionScopes.aiConfigRead}>
                            <AiSettingsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/ai/activity" element={
                          <PermissionGuard anyOf={[PermissionCodes.adminAccess]}>
                            <AiActivityPage />
                          </PermissionGuard>
                        } />
                        <Route path="/ai-usage" element={
                          <PermissionGuard anyOf={PermissionScopes.aiTokenUsageRead}>
                            <AiUsagePage />
                          </PermissionGuard>
                        } />
                        <Route path="/knowledge" element={<Navigate to="/ai/knowledge" replace />} />
                        <Route path="/settings/ai" element={<Navigate to="/ai/config" replace />} />
                        <Route path="/tasks/add" element={
                          <PermissionGuard anyOf={[PermissionCodes.tasksManage]}>
                            <TaskCreateRedirect />
                          </PermissionGuard>
                        } />
                        <Route path="/tasks/:id" element={
                          <PermissionGuard anyOf={[PermissionCodes.tasksManage]}>
                            <TaskEditor />
                          </PermissionGuard>
                        } />
                        <Route path="/tasks/:id/edit" element={
                          <PermissionGuard anyOf={[PermissionCodes.tasksManage]}>
                            <Navigate to="../" relative="path" replace />
                          </PermissionGuard>
                        } />
                        <Route path="/calendar/availability" element={<Navigate to="/calendar" replace />} />
                        <Route path="/calendar/events" element={<Navigate to="/calendar" replace />} />
                        <Route path="/calendar/rules" element={<Navigate to="/calendar" replace />} />
                        <Route path="/conversations" element={
                          <PermissionGuard anyOf={PermissionScopes.conversationsRead}>
                            <ConversationsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/conversations/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.conversationsRead}>
                            <ConversationsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/web-widget-demo" element={
                          <PermissionGuard anyOf={PermissionScopes.conversationsRead}>
                            <WebWidgetDemoPage />
                          </PermissionGuard>
                        } />
                        <Route path="/admin" element={<Navigate to={adminHomePath} replace />} />
                        <Route path="/admin/companies" element={
                          <PermissionGuard anyOf={PermissionScopes.companiesRead}>
                            <CompaniesPage />
                          </PermissionGuard>
                        } />
                        <Route path="/admin/connectors" element={
                          <PermissionGuard anyOf={PermissionScopes.connectorsRead}>
                            <ConnectorsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/automations" element={
                          <PermissionGuard anyOf={PermissionScopes.conversationsRead}>
                            <AutomationsPage />
                          </PermissionGuard>
                        } />
                        {/* Automations moved out of the admin area; the tab lives in the query string. */}
                        <Route path="/admin/automations" element={<RedirectToAutomations />} />
                        <Route
                          path="/admin/comment-automations"
                          element={<Navigate to="/automations?tab=comments" replace />}
                        />
                        <Route
                          path="/admin/dm-automations"
                          element={<Navigate to="/automations?tab=dms" replace />}
                        />
                        <Route path="/admin/roles" element={
                          <PermissionGuard anyOf={PermissionScopes.rolesRead}>
                            <RolesPage />
                          </PermissionGuard>
                        } />
                        <Route path="/admin/security" element={
                          <PermissionGuard anyOf={[PermissionCodes.adminAccess]}>
                            <SecurityPage />
                          </PermissionGuard>
                        } />
                        <Route path="/admin/auth/revoke-refresh-tokens" element={
                          <PermissionGuard anyOf={[PermissionCodes.adminAccess]}>
                            <RefreshTokenOperationsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/admin/team-members" element={<Navigate to="/members" replace />} />
                        <Route path="/companies" element={<Navigate to="/admin/companies" replace />} />
                        <Route path="/members" element={
                          <PermissionGuard anyOf={PermissionScopes.employeesRead}>
                            <TeamMembers />
                          </PermissionGuard>
                        } />
                        <Route path="/members/:id" element={
                          <PermissionGuard anyOf={PermissionScopes.employeesRead}>
                            <EmployeeDetails />
                          </PermissionGuard>
                        } />
                        <Route path="/profile" element={<MyProfilePage />} />
                        <Route path="/plans" element={<PlansPage />} />
                        <Route path="/add" element={<Navigate to="/members/add" replace />} />
                        <Route path="/members/add" element={
                          <PermissionGuard anyOf={[PermissionCodes.employeesManage]}>
                            <AddEmployee />
                          </PermissionGuard>
                        } />
                        <Route path="/calls" element={
                          <PermissionGuard anyOf={PermissionScopes.conversationsRead}>
                            <CallsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/calls/inbound-pop" element={
                          <PermissionGuard anyOf={PermissionScopes.conversationsRead}>
                            <InboundCallPopPage />
                          </PermissionGuard>
                        } />
                        <Route path="/unauthorized" element={<UnauthorizedPage />} />
                      </Routes>
                    </main>
                  </div>
                  <FloatingChat />
                  <CallCenter />
                </div>
              </ChatProvider>
            )
          }
        />
      </Routes>
      )}
    </Router>
  );
}

export default App;
